import { animate, Easing } from 'motion/react';

export type ClassValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | ClassValue[]
  | Record<string, boolean | null | undefined>;

/**
 * Join class names together, ignoring falsy values. Accepts strings,
 * conditionals, arrays and objects keyed by class name.
 */
export function cn(...args: ClassValue[]) {
  const classes: string[] = [];

  for (const arg of args) {
    if (!arg) continue;

    if (typeof arg === 'string' || typeof arg === 'number') {
      classes.push(String(arg));
    } else if (Array.isArray(arg)) {
      const nested = cn(...arg);
      if (nested) classes.push(nested);
    } else if (typeof arg === 'object') {
      for (const [key, value] of Object.entries(arg)) {
        if (value) classes.push(key);
      }
    } else if (arg === true) {
      // A bare `true` carries no class name
      continue;
    } else {
      // Unreachable unless `ClassValue` grows a member, which fails the build
      const unsupported: never = arg;
      throw new TypeError(
        `cn received an unsupported class value of type ${typeof unsupported}`
      );
    }
  }

  return classes.join(' ');
}

export const easeOutExpo: Easing = [0.16, 1, 0.3, 1];
export const easeOutSmooth: Easing = [0.165, 0.84, 0.44, 1];

export function shake(element: HTMLElement | null) {
  if (!element) return;
  animate(element, { x: [-2, 2, -2, 2, 0] }, { duration: 0.3 });
}

/**
 * Used to defer work (like focusing elements) until the next frame.
 */
export function nextFrame(callback: () => void) {
  setTimeout(callback, 0);
}

/**
 * Convert a string to title case
 */
export function titleCase(str: string) {
  return str
    .split(' ')
    .map((word) => word.charAt(0).toLocaleUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Convert a string to sentence case
 */
export function sentenceCase(str: string) {
  return str
    .split(' ')
    .map((word, index) => {
      // Always title case the first word
      if (index === 0) {
        return titleCase(word);
      }

      // If first two letters are uppercase (likely acronym), return it as is
      const prefix = word.slice(0, 2);
      if (prefix.toLocaleUpperCase() === prefix) {
        return word;
      }

      // Otherwise, return the word in sentence case
      return word.charAt(0).toLocaleLowerCase() + word.slice(1);
    })
    .join(' ');
}

/**
 * Determine the correct article ("a" or "an") to use before the given string
 */
export function getArticle(str: string) {
  // If the first word is empty, return 'a'
  const firstWord = str.split(' ').at(0);
  if (!firstWord) return 'a';

  // If the first word starts with a vowel or is an acronym, return 'an'
  if (/^[aeiou]/i.test(firstWord) || /^[A-Z]([A-Z.]+)?$/.test(firstWord)) {
    return 'an';
  }

  // Otherwise, return 'a'
  return 'a';
}
